import { API } from "aws-amplify";

import Lang from "@/lang";
import {
  AWS_SERVICE_NAME,
  RequestMethods,
  apiErrorCodes,
  apiErrorStatuses,
  requestStatusesDictionary,
} from "@/settings/apiRoutes";
import store from "@/store";
import { NoDataStatusItem } from "@/store/modules/charts";

interface Config<T> {
  path: string;
  defaultResult: T;
  method?: RequestMethods;
  noDataItem?: NoDataStatusItem;
  errorText?: string;
  // Wide variants of requests payloads.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  errorCallback?: (statusCode?: number, error?: any) => void;
  successCallback?: () => void;
  token: string;
  silent?: boolean;
  // Wide variants of requests payloads.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  body?: { [key: string]: any };
  params?: { [key: string]: any };
  throwError?: boolean;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export const REQUEST_CANCEL_MARK = "request_canceled";

const BACKEND_MESSAGE_STATUSES: number[] = [409];

function extractStatus(err: unknown): number | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  return e?.response?.status ?? e?.status;
}
function extractBackendMessage(err: unknown): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = err as any;
  return e?.response?.data?.message || e?.response?.data?.detail || null;
}

export default class Api {
  static async request<T>(config: Config<T>): Promise<T> {
    const method = (config.method || "get").toLowerCase();
    const params = method === "get" ? { queryStringParameters: config.params || {} } : { body: config.body || {} };

    const timestamp = Date.now();
    store.commit("charts/updateRequestStatus", {
      timestamp,
      method: config.method,
      path: config.path,
      token: config.token,
      status: requestStatusesDictionary.sent,
    });

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res = (API as Record<string, any>)[method as string](AWS_SERVICE_NAME, config.path, params);

      store.commit("app/pushRequest", { token: config.token, promise: res });

      const data = await res;

      if (typeof config.successCallback === "function") {
        config.successCallback();
      }

      store.commit("charts/updateRequestStatus", {
        timestamp,
        status: requestStatusesDictionary.answered,
      });

      return data;
    } catch (err: unknown) {
      store.commit("charts/updateRequestStatus", {
        timestamp,
        status: requestStatusesDictionary.error,
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;

      if (e?.message === REQUEST_CANCEL_MARK) {
        return config.defaultResult;
      }

      if (config.noDataItem) {
        store.commit("charts/updateNoDataStatus", config.noDataItem);
      }

      if (typeof config.errorCallback === "function") {
        const statusCode = extractStatus(e);
        config.errorCallback(statusCode, e);
      }

      const statusCode = extractStatus(e);
      const statusError = apiErrorStatuses.find((el) => el.status === statusCode);
      const codeError = apiErrorCodes.find((el) => el.code === e?.response?.data?.error_code);

      const allowBackendMessage = statusCode !== undefined && BACKEND_MESSAGE_STATUSES.includes(statusCode);
      const backendMessage = allowBackendMessage ? extractBackendMessage(e) : null;

      const alertMessage =
        config.errorText || backendMessage || statusError?.message || codeError?.message || Lang.unknownError;

      if (!config.silent) {
        store.commit("app/showAlert", {
          message: alertMessage,
          type: "error",
        });
      }

      if (config.throwError) {
        throw e;
      }

      return config.defaultResult;
    }
  }

  static async requestWithRetry<T>(config: Config<T>, delayMs = 3000): Promise<T> {
    try {
      return await Api.request<T>({ ...config, silent: true, throwError: true });
    } catch {
      await new Promise((r) => setTimeout(r, delayMs));
      return Api.request<T>({ ...config, silent: true });
    }
  }
}
